import { IConvertResult, IBindings } from './interfaces/patternsConverter';
import { PatternsReader } from './PatternsReader';
import { ParsedType } from './interfaces/parsedHtml';
import { HtmlNodesHelper } from './HtmlNodesHelper';
import { IPartialChildNodeWithIndex, NodeName } from './interfaces/patternsHtmlTags.interface';
import { config } from './config/config';
import { tagToReadable } from '../../../../utils/tagToReadable';
import { PatternsConfig } from './PatternsConfig';

export class PatternsConverter {
  private nodeReader: PatternsReader;
  private config = new PatternsConfig(config);

  constructor(private htmlNodes: IPartialChildNodeWithIndex[]) {
    this.nodeReader = new PatternsReader(this.htmlNodes);
  }

  private tagsBindings: IBindings = {
    P: 'p',
    FIGURE: 'figure',
    H1: 'commonNode',
    H2: 'commonNode',
    H3: 'commonNode',
    H4: 'commonNode',
    OL: 'commonNode',
    UL: 'commonNode',
    BLOCKQUOTE: 'commonNode',
  };

  setHtmlNodes(htmlNodes: NodeListOf<ChildNode>): void {
    this.htmlNodes = HtmlNodesHelper.convertToPartialWithIndex(htmlNodes);
    this.nodeReader.setHtmlNodes(this.htmlNodes);
  }

  convert(index: number): IConvertResult {
    const currentNode = this.htmlNodes[index];

    const { parsed, jumpIndexCount } = this[
      this.tagsBindings[currentNode.nodeName as NodeName] || 'commonNode'
    ](index, currentNode);

    return {
      jumpIndexCount,
      parsed: parsed?.amount !== 0
        ? this.isToIgnore(parsed?.type as ParsedType)
          ? undefined
          : parsed
        : undefined,
    };
  }

  private isToIgnore(parsedType: ParsedType): boolean {
    const { tagsToIgnore } = this.config.getGeneral();
    if (tagsToIgnore.includes(parsedType)) {
      return true;
    }
    return false;
  }

  private p(index: number): IConvertResult {
    const paragraphs = this.nodeReader.readSequencial(index, 'P');
    const filledParagraphs = HtmlNodesHelper.removeEmpty(paragraphs);

    return {
      jumpIndexCount: paragraphs.length,
      parsed: {
        amount: filledParagraphs.length,
        line: HtmlNodesHelper.getLinesFrom(paragraphs),
        readableName: 'paragraph',
        type: 'PARAGRAPH',
      },
    };
  }

  private figure(index: number): IConvertResult {
    const firstFigure = this.nodeReader.get(index);
    const typeOfFirstFigure = this.getFigureType(firstFigure);

    const filter = (node: IPartialChildNodeWithIndex) => {
      if (node.nodeName !== 'FIGURE') return false;

      const type = this.getFigureType(node);
      if (type !== typeOfFirstFigure) return false;

      return true;
    };

    const figures = this.nodeReader.readSequencialWithFilter(index, filter);

    return {
      jumpIndexCount: figures.length,
      parsed: {
        amount: figures.length,
        line: HtmlNodesHelper.getLinesFrom(figures),
        readableName: typeOfFirstFigure === 'VIDEO' ? 'video' : 'image',
        type: typeOfFirstFigure,
      },
    };
  }

  private getFigureType(figure: IPartialChildNodeWithIndex): 'VIDEO' | 'IMAGE' {
    if (Array.from(figure.childNodes).find(child => child.nodeName === 'IMG')) {
      return 'IMAGE';
    }
    return 'VIDEO';
  }

  private commonNode(index: number, node: IPartialChildNodeWithIndex): IConvertResult {
    const makeTagsUnique = this.config.getGeneral('makeTagsUnique');
    const nodes = this.nodeReader.readSequencial(index, node.nodeName as NodeName);

    const nodesCount = makeTagsUnique.includes(node.nodeName as ParsedType) ? 1 : nodes.length;

    return {
      jumpIndexCount: nodesCount,
      parsed: {
        amount: nodesCount,
        line: HtmlNodesHelper.getLinesFrom(nodes),
        readableName: tagToReadable(node.nodeName as ParsedType) || node.nodeName.toLowerCase(),
        type: node.nodeName as ParsedType,
      },
    };
  }
}
